import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export const USERS_API = environment.api + 'users-settings';

interface UserRow {
    user_id: number;
    user_first_name: string;
    user_last_name: string;
    user_email: string;
    user_level_name: string;
    branch_name: string;
    user_active: number;
    user_is_treater: number;
    user_last_login_at: string;
    photo_url: string;
}

/**
 * Settings > Users & Permissions > Users (mockup settings #usersPanel; twin-ci users_list).
 * Administrators only (the API checks it). Server paging (twin-ci paged at 25), search by name /
 * email, Role and Status filters, Last Active = last sign-in (recorded since 2026-10-12).
 * Kept from twin-ci: tick users and "Email Selected" opens twin-ci's email compose for them.
 */
@Component({
    selector: 'app-users-list',
    templateUrl: './users-list.component.html',
    styleUrls: ['./users-list.component.scss']
})
export class UsersListComponent implements OnInit, OnDestroy {
    rows: UserRow[] = [];
    levels: { user_level_id: number, user_level_name: string }[] = [];
    total = 0;
    page = 1;
    perPage = 25;
    search = '';
    level = '';
    status = 'all';
    loading = false;
    loaded = false;
    error = '';
    message = '';
    picked: { [id: number]: boolean } = {};
    deleting: UserRow = null;
    saving = false;
    readonly composeUrl = environment.oldSite + 'index.php?/administrator/email_compose/users';
    private searches = new Subject<string>();
    private subscription: Subscription;

    constructor(private http: HttpClient) { }

    ngOnInit() {
        this.subscription = this.searches.pipe(debounceTime(300)).subscribe(() => { this.page = 1; this.load(); });
        this.http.get<any>(USERS_API + '/options').subscribe(res => this.levels = res.levels || []);
        this.load();
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    onSearch() {
        this.searches.next(this.search);
    }

    filter() {
        this.page = 1;
        this.load();
    }

    load() {
        this.loading = true;
        const params: any = { page: this.page, per_page: this.perPage, search: this.search, status: this.status };
        if (this.level) { params.level = this.level; }
        this.http.get<any>(USERS_API, { params }).subscribe(
            res => {
                this.rows = res.data || [];
                this.total = res.total;
                this.loading = false;
                this.loaded = true;
            },
            err => {
                this.loading = false;
                this.error = typeof err === 'string' ? err : 'The users could not be loaded.';
            }
        );
    }

    get pages() {
        return Math.max(1, Math.ceil(this.total / this.perPage));
    }

    get pageInfo() {
        if (!this.total) { return 'No users'; }
        const from = (this.page - 1) * this.perPage + 1;
        return 'Showing ' + from + '–' + Math.min(this.total, this.page * this.perPage) + ' of ' + this.total + ' users';
    }

    goTo(page: number) {
        if (page < 1 || page > this.pages) { return; }
        this.page = page;
        this.load();
    }

    name(r: UserRow) {
        return [r.user_first_name, r.user_last_name].filter(x => x).join(' ');
    }

    initials(r: UserRow) {
        return ((r.user_first_name || '').charAt(0) + (r.user_last_name || '').charAt(0)).toUpperCase() || '?';
    }

    lastActive(r: UserRow) {
        if (!r.user_last_login_at) { return 'Never'; }
        const d = new Date(r.user_last_login_at.replace(' ', 'T'));
        const today = new Date();
        const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        if (d.toDateString() === today.toDateString()) { return 'Today, ' + time; }
        const yesterday = new Date(today.getTime() - 86400000);
        if (d.toDateString() === yesterday.toDateString()) { return 'Yesterday, ' + time; }
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }

    get pickedIds(): number[] {
        return Object.keys(this.picked).filter(k => this.picked[k]).map(Number);
    }

    pickPage(on: boolean) {
        this.rows.forEach(r => this.picked[r.user_id] = on);
    }

    get pageAllPicked() {
        return this.rows.length > 0 && this.rows.every(r => this.picked[r.user_id]);
    }

    remove() {
        this.saving = true;
        this.http.delete<any>(USERS_API + '/' + this.deleting.user_id).subscribe(
            res => {
                this.saving = false;
                this.message = res.message;
                delete this.picked[this.deleting.user_id];
                this.deleting = null;
                this.load();
            },
            err => {
                this.saving = false;
                this.deleting = null;
                this.error = typeof err === 'string' ? err : 'The user could not be deleted.';
            }
        );
    }
}
